import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import ProfileInformation from "../../components/profileInformation/ProfileInformation";
import AccountActivity from "../../components/accountActivity/AccountActivity";
import Security from "../../components/security/Security";
import BankInformation from "../../components/bankInformation/BankInformation";
import LogoutConfirmationModal from "../../Modals/logoutConfirmationModal/LogoutConfirmationModal";
import Header from "../../components/header/Header";
import RefundRequest from "../../components/refundRequest/RefundRequest";
import "./Account.css";
import SwitchAccount from "../../components/switchAccount/SwitchAccount";
import ProfilePicturePage from "../../OnboardingPage/ProfilePicturePage";

const Account = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const tabRefs = useRef({});
  const tabsContainerRef = useRef(null);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollWidth > container.clientWidth;

    if (hasOverflow) {
      const hintTimer = setTimeout(() => {
        container.scrollTo({
          left: 120,
          behavior: "smooth",
        });

        const returnTimer = setTimeout(() => {
          container.scrollTo({
            left: 0,
            behavior: "smooth",
          });
        }, 1000);

        return () => clearTimeout(returnTimer);
      }, 500);

      return () => clearTimeout(hintTimer);
    }
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const updateFadeIndicators = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;

      setShowLeftFade(scrollLeft > 10);

      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 10);
    };

    updateFadeIndicators();

    container.addEventListener("scroll", updateFadeIndicators);

    window.addEventListener("resize", updateFadeIndicators);

    return () => {
      container.removeEventListener("scroll", updateFadeIndicators);
      window.removeEventListener("resize", updateFadeIndicators);
    };
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    const activeTabEl = tabRefs.current[activeTab];

    if (container && activeTabEl) {
      requestAnimationFrame(() => {
        const scrollLeft =
          activeTabEl.offsetLeft -
          container.offsetWidth / 2 +
          activeTabEl.offsetWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      });
    }
  }, [activeTab]);

  if (!user) {
    navigate("/");
    return null;
  }

  const activeRole = user?.activeRole;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);

    if (tabRefs.current[tab]) {
      tabRefs.current[tab].scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <ProfileInformation
            user={user}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        );
      // case "activity":
      //   return <AccountActivity user={user} />;
      case "security":
        return <Security user={user} />;
      case "bank":
        return <BankInformation user={user} />;
      case "refund":
        return <RefundRequest user={user} />;
      case "becomeHost":
        return (
          <ProfilePicturePage
            user={user}
            source={"BECOME-HOST"}
            onBecomeHostComplete={() => setActiveTab("switchAccounts")}
          />
        );
      case "switchAccounts":
        return <SwitchAccount onSwitched={() => setActiveTab("profile")} />;
      default:
        return (
          <ProfileInformation
            user={user}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        );
    }
  };

  return (
    <div className="account-tab-main-page">
      <Header />
      <div className="account-tab-main-content">
        <div className="account-tab-page">
          <div className="account-tab-header">
            <h1>Account</h1>
          </div>

          <div className="account-tab-tabs-wrapper">
            {showLeftFade && <div className="account-tab-fade-left" />}
            {showRightFade && <div className="account-tab-fade-right" />}

            <div className="account-tab-tabs" ref={tabsContainerRef}>
              <button
                ref={(el) => (tabRefs.current["profile"] = el)}
                className={`account-tab-button ${
                  activeTab === "profile" ? "account-tab-active" : ""
                }`}
                onClick={() => handleTabClick("profile")}
              >
                Profile Information
              </button>
              {/* <button
                ref={(el) => (tabRefs.current["activity"] = el)}
                className={`account-tab-button ${
                  activeTab === "activity" ? "account-tab-active" : ""
                }`}
                onClick={() => handleTabClick("activity")}
              >
                Account Activity
              </button> */}
              <button
                ref={(el) => (tabRefs.current["security"] = el)}
                className={`account-tab-button ${
                  activeTab === "security" ? "account-tab-active" : ""
                }`}
                onClick={() => handleTabClick("security")}
              >
                Security
              </button>

              {activeRole !== "usher" && (
                <>
                  <button
                    ref={(el) => (tabRefs.current["refund"] = el)}
                    className={`account-tab-button ${
                      activeTab === "refund" ? "account-tab-active" : ""
                    }`}
                    onClick={() => handleTabClick("refund")}
                  >
                    Refund
                  </button>
                </>
              )}
              {activeRole !== "host" && !user?.role.includes("host") && (
                <>
                  <button
                    ref={(el) => (tabRefs.current["becomeHost"] = el)}
                    className={`account-tab-button ${
                      activeTab === "becomeHost" ? "account-tab-active" : ""
                    }`}
                    onClick={() => handleTabClick("becomeHost")}
                  >
                    Become Host
                  </button>
                </>
              )}

              {user?.role.length > 1 && (
                <>
                  <button
                    ref={(el) => (tabRefs.current["switchAccounts"] = el)}
                    className={`account-tab-button ${
                      activeTab === "switchAccounts" ? "account-tab-active" : ""
                    }`}
                    onClick={() => handleTabClick("switchAccounts")}
                  >
                    Switch Accounts
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="account-tab-content">{renderTabContent()}</div>
        </div>
      </div>

      <LogoutConfirmationModal
        visible={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default Account;
